import { groupMembers, questionsForAnswers } from "./groups";
import { canCompareAnswers } from "./answerComparison";
import { questionIdSchema } from "./ids";
import {
  MAX_REPEAT_FIELDS,
  MAX_REPEAT_ENTRIES,
  repeatFieldsOf,
} from "./repeatable";
import {
  INTEGER_RULE_MINIMUMS,
  VALIDATION_RULE_LABELS,
  type Condition,
  type FormContent,
  type ValidationRule,
} from "./types";
import {
  MAX_FILE_SIZE_MB_CEILING,
  MAX_FILES_CEILING,
  MIN_FILE_SIZE_MB,
  canConditionOn,
  definitionFor,
  takesAnswer,
  maxFileSizeMbOf,
  maxFilesOf,
  offersOther,
  optionsOf,
  rowsOf,
} from "./registry";
import {
  orderedQuestions,
  orderedSections,
  sectionsAlwaysVisitedBefore,
  questionsInFormOrder,
  questionsBefore,
  validateJumpGraph,
  evaluateCondition,
} from "./logic";

/**
 * Publishing freezes a draft into an immutable version. Editing the draft
 * afterwards never changes a version that has already been published.
 *
 * Everything here is the gate in front of that: what makes a draft fit to
 * publish, and what the author has to fix first.
 */

/**
 * The quantifiers that make a repeat unbounded: `+`, `*`, and an open-ended
 * brace `{2,}`. A bounded `{2}` or `{2,5}` expands to strictly finite work,
 * so `((ab){2}){3}` is a legitimate pattern, not a catastrophe — treating
 * every `{` as dangerous blocked exactly the fixed-count patterns an author
 * writes on purpose.
 */
const UNBOUNDED = String.raw`(?:[+*]|\{\d+,\})`;

/**
 * A repeat wrapped around a group that already repeats — `(a+)+`, `(a*)*`,
 * `(a|b)+*` — the shape behind catastrophic backtracking. Deliberately a
 * heuristic: it catches the accidental cases an author actually writes.
 */
const NESTED_QUANTIFIER = new RegExp(
  String.raw`\((?:[^()\\]|\\.)*${UNBOUNDED}\)\s*${UNBOUNDED}`,
);

/**
 * A quantified group whose contents already contain a quantified group —
 * `((a)+)+`, `(?:(ab)*x)+` — the nested-group spelling of the shape above,
 * which the flat test can't see because the inner parentheses stop its scan.
 */
const NESTED_GROUP_QUANTIFIER = new RegExp(
  String.raw`\((?:[^()\\]|\\.)*\((?:[^()\\]|\\.)*\)${UNBOUNDED}(?:[^()\\]|\\.)*\)\s*${UNBOUNDED}`,
);

/**
 * A quantified group containing an alternation whose branches share a prefix —
 * `(a|aa)+`, `(x|xy)*` — the other everyday catastrophic shape, and one the
 * nested-quantifier test doesn't see.
 */
function hasOverlappingAlternation(pattern: string): boolean {
  for (const match of pattern.matchAll(/\(([^()]*\|[^()]*)\)\s*[+*]/g)) {
    const branches = (match[1] ?? "").split("|").map((b) => b.trim());
    for (const a of branches) {
      for (const b of branches) {
        if (a === b || a === "" || b === "") continue;
        if (a.startsWith(b) || b.startsWith(a)) return true;
      }
    }
  }
  return false;
}

/**
 * A pattern whose worst case is exponential — the shapes behind catastrophic
 * backtracking, as far as the two heuristics above can see them.
 *
 * One predicate shared by publishing (which refuses the pattern with an
 * explanation) and answer validation (which skips it, exactly as it skips a
 * pattern that doesn't compile): the check runs synchronously on the server
 * as well as in the tab, so a pattern either side would choke on must never
 * reach `RegExp.test` anywhere.
 */
export function isCatastrophicPattern(pattern: string): boolean {
  return (
    NESTED_QUANTIFIER.test(pattern) ||
    NESTED_GROUP_QUANTIFIER.test(pattern) ||
    hasOverlappingAlternation(pattern)
  );
}

export interface PublishIssue {
  message: string;
  severity: "error" | "warning";
  /** Stable owner identities for comparing issues across draft edits. */
  groupId?: string;
  fieldId?: string;
  questionId?: string;
  sectionId?: string;
}

export interface PublishCheck {
  ok: boolean;
  errors: string[];
  warnings: string[];
  issues: PublishIssue[];
}

/**
 * Whether a draft may be published.
 *
 * A jump loop blocks; an unreachable section warns. See `validateJumpGraph`.
 */
export function checkPublishable(form: FormContent): PublishCheck {
  const groupIssues: PublishIssue[] = [];
  const reportGroup = (
    message: string,
    groupId?: string,
    questionId?: string,
  ) => {
    const group = form.groups?.find((g) => g.id === groupId);
    groupIssues.push({
      message,
      severity: "error",
      sectionId: group?.sectionId,
      groupId,
      questionId,
    });
  };
  const groupIds = new Set<string>();
  for (const group of form.groups ?? []) {
    if (
      groupIds.has(group.id) ||
      form.questions.some((q) => q.id === group.id) ||
      !questionIdSchema.safeParse(group.id).success
    )
      reportGroup(
        "Groups need unique, valid identities distinct from questions.",
        group.id,
      );
    groupIds.add(group.id);
    const members = groupMembers(form, group);
    if (!group.title.trim()) reportGroup("Give every group a title.", group.id);
    if (!form.sections.some((s) => s.id === group.sectionId))
      reportGroup(
        `“${group.title}” belongs to a section that no longer exists.`,
        group.id,
      );
    if (!members.length)
      reportGroup(
        `“${group.title}” has no questions. Ungroup it or add questions.`,
        group.id,
      );
    const siblings = orderedQuestions(form, group.sectionId);
    const positions = members.map((q) =>
      siblings.findIndex((s) => s.id === q.id),
    );
    if (positions.some((p, i) => i > 0 && p !== positions[i - 1]! + 1))
      reportGroup(`Keep the questions in “${group.title}” together.`, group.id);
    if (group.repeat) {
      if (members.some((q) => q.type === "repeatable_group"))
        reportGroup("Repeats cannot contain other repeats.", group.id);
    }
  }
  for (const question of form.questions) {
    if (
      question.groupId &&
      !form.groups?.some(
        (g) => g.id === question.groupId && g.sectionId === question.sectionId,
      )
    )
      reportGroup(
        `“${question.label}” belongs to a missing group or a group in another section.`,
        question.groupId,
        question.id,
      );
  }
  form = { ...form, questions: questionsForAnswers(form), groups: undefined };
  const errors: string[] = groupIssues
    .filter((i) => i.severity === "error")
    .map((i) => i.message);
  const warnings: string[] = [];
  const issues: PublishIssue[] = [...groupIssues];
  function addError(
    message: string,
    target: Pick<PublishIssue, "questionId" | "sectionId" | "fieldId"> = {},
  ) {
    errors.push(message);
    issues.push({ message, severity: "error", ...target });
  }

  if (!form.title.trim()) addError("Give the form a title.");
  if (form.sections.length === 0) addError("Add at least one section.");
  if (form.questions.length === 0) addError("Add at least one question.");

  const sectionIds = new Set(form.sections.map((s) => s.id));
  for (const question of form.questions) {
    if (!question.label.trim()) {
      addError("Every question needs a label.", {
        questionId: question.id,
        sectionId: question.sectionId,
      });
    }
  }
  for (const question of form.questions) {
    if (!sectionIds.has(question.sectionId)) {
      addError(`“${question.label}” isn't in any section.`, {
        questionId: question.id,
        sectionId: question.sectionId,
      });
    }
  }

  const questionIds = new Set(form.questions.map((q) => q.id));
  for (const question of form.questions) {
    const source = question.visibleWhen?.questionId;
    if (source && !questionIds.has(source)) {
      addError(
        `“${question.label}” depends on a question that no longer exists.`,
        { questionId: question.id, sectionId: question.sectionId },
      );
    }
    if (source === question.id) {
      addError(`“${question.label}” can't depend on its own answer.`, {
        questionId: question.id,
        sectionId: question.sectionId,
      });
    } else if (source) {
      // Document order, not the raw sort key: `pos` only orders questions
      // within a section, so a condition can otherwise point at a question the
      // applicant hasn't reached yet.
      const earlier = questionsBefore(form, question.id);
      if (!earlier.some((q) => q.id === source)) {
        addError(
          `“${question.label}” depends on an answer the applicant hasn't given yet.`,
          { questionId: question.id, sectionId: question.sectionId },
        );
      }
    }
  }

  // A question can be well-formed and still impossible to answer. The builder
  // normalizes as you type, but publishing is the gate that matters — a form
  // reaching an applicant in this state can't be submitted at all.
  const CHOICE_TYPES = ["multiple_choice", "checkboxes", "dropdown"] as const;
  for (const question of form.questions) {
    if (
      question.type === "acknowledgment" &&
      question.config?.acknowledgmentLabel?.trim() === ""
    ) {
      addError(`“${question.label}” needs an acknowledgment statement.`, {
        questionId: question.id,
        sectionId: question.sectionId,
      });
    }
    if ((CHOICE_TYPES as readonly string[]).includes(question.type)) {
      const options = optionsOf(question);
      if (options.length === 0) {
        addError(`“${question.label}” has no options to choose from.`, {
          questionId: question.id,
          sectionId: question.sectionId,
        });
      }
      // Duplicate *labels*, not ids: ids are unique by construction, but two
      // options reading the same is a choice an applicant can't make sense of
      // and an answer nobody can interpret afterwards.
      const labels = options.map((option) => option.label.trim());
      if (new Set(labels).size !== labels.length) {
        addError(`“${question.label}” lists the same option twice.`, {
          questionId: question.id,
          sectionId: question.sectionId,
        });
      }
    }
    // A rule the type doesn't understand is silently skipped when answers are
    // validated, while the builder still lists it — so the author believes a
    // check is in force that isn't. Changing a type drops the rules that no
    // longer apply, but a rule added concurrently on another client survives
    // that merge, because there was nothing there to drop when it happened.
    const supported = definitionFor(question.type).validators;
    for (const rule of question.validation ?? []) {
      if (!supported.includes(rule.kind)) {
        addError(
          `“${question.label}” carries a rule that ${definitionFor(question.type).label.toLowerCase()} answers can't apply. Remove it, or change the question's type back.`,
          { questionId: question.id, sectionId: question.sectionId },
        );
        // Removing the rule resolves a bad value with it — one instruction.
        continue;
      }
      // A value the content schema refuses must be refused here too, or the
      // Publish button stays enabled for a draft the server will turn away —
      // and the server's parse failure can't name the rule at fault.
      switch (rule.kind) {
        case "compareAnswers": {
          if (rule.comparisons.length === 0)
            addError(`“${question.label}” needs an answer to compare with.`, {
              questionId: question.id,
              sectionId: question.sectionId,
            });
          for (const comparison of rule.comparisons) {
            const source = form.questions.find(
              (q) => q.id === comparison.questionId,
            );
            const earlier = questionsBefore(form, question.id).some(
              (q) => q.id === comparison.questionId,
            );
            if (
              !source ||
              !earlier ||
              !canCompareAnswers(form, question, source)
            ) {
              addError(
                `“${question.label}” must compare with an earlier, non-repeated ${question.type === "money" ? "money" : "full date"} question.`,
                { questionId: question.id, sectionId: question.sectionId },
              );
            }
            const years = comparison.years ?? 0;
            if (
              !Number.isInteger(years) ||
              years < 0 ||
              years > 100 ||
              (years !== 0 &&
                (question.type !== "date" || comparison.op !== "atLeast"))
            ) {
              addError(
                `“${question.label}” can add 0–100 whole calendar years only to a date's lower bound.`,
                { questionId: question.id, sectionId: question.sectionId },
              );
            }
          }
          break;
        }
        case "minLength":
        case "maxLength":
        case "minSelected":
        case "maxSelected": {
          const minimum = INTEGER_RULE_MINIMUMS[rule.kind];
          if (!Number.isInteger(rule.value) || rule.value < minimum) {
            addError(
              `“${question.label}” has a “${VALIDATION_RULE_LABELS[rule.kind]}” rule that needs a whole number of at least ${minimum}.`,
              { questionId: question.id, sectionId: question.sectionId },
            );
          }
          break;
        }
        case "min":
        case "max":
          if (!Number.isFinite(rule.value)) {
            addError(
              `“${question.label}” has a “${VALIDATION_RULE_LABELS[rule.kind]}” rule without a usable number.`,
              { questionId: question.id, sectionId: question.sectionId },
            );
          }
          break;
        default:
          break;
      }
    }

    // Rules that can never all hold leave a required question with no
    // submittable answer: blank fails `required`, and anything else fails a
    // bound. Same failure as a choice question with no options.
    const rules = question.validation ?? [];
    if (question.type === "date") {
      // Collect across rules too: imported drafts can contain more than one
      // compareAnswers rule. A positive anniversary cannot precede its source.
      const comparisons = rules.flatMap((rule) =>
        rule.kind === "compareAnswers" ? rule.comparisons : [],
      );
      const upperSources = new Set(
        comparisons
          .filter((comparison) => comparison.op === "atMost")
          .map((comparison) => comparison.questionId),
      );
      if (
        comparisons.some(
          (comparison) =>
            comparison.op === "atLeast" &&
            (comparison.years ?? 0) > 0 &&
            upperSources.has(comparison.questionId),
        )
      ) {
        addError(
          `“${question.label}” has date bounds that can't both be satisfied: a date cannot be after an anniversary and on or before the same source date.`,
          { questionId: question.id, sectionId: question.sectionId },
        );
      }
    }
    const ruleValue = (kind: ValidationRule["kind"]): number | undefined => {
      const rule = rules.find((r) => r.kind === kind);
      // `contains` also carries a `value`, but a string one — only the numeric
      // bounds are comparable.
      if (!rule || !("value" in rule) || typeof rule.value !== "number") {
        return undefined;
      }
      return rule.value;
    };
    const pairs: Array<
      [ValidationRule["kind"], ValidationRule["kind"], string]
    > = [
      ["minLength", "maxLength", "character counts"],
      ["min", "max", "values"],
      ["minSelected", "maxSelected", "how many can be ticked"],
    ];
    for (const [lowKind, highKind, what] of pairs) {
      const low = ruleValue(lowKind);
      const high = ruleValue(highKind);
      if (low !== undefined && high !== undefined && low > high) {
        addError(
          `“${question.label}” asks for ${what} that can't both be satisfied.`,
          { questionId: question.id, sectionId: question.sectionId },
        );
      }
    }
    const minSelected = ruleValue("minSelected");
    if (
      minSelected !== undefined &&
      (CHOICE_TYPES as readonly string[]).includes(question.type) &&
      minSelected > optionsOf(question).length
    ) {
      addError(`“${question.label}” asks for more choices than it offers.`, {
        questionId: question.id,
        sectionId: question.sectionId,
      });
    }

    if (question.type === "file_upload") {
      // The config schema keeps every field optional and unbounded so a type
      // change never invalidates a question; publishing is where the bounds
      // bite, the same as the linear scale's.
      const maxFiles = maxFilesOf(question);
      if (
        !Number.isInteger(maxFiles) ||
        maxFiles < 1 ||
        maxFiles > MAX_FILES_CEILING
      ) {
        addError(
          `“${question.label}” must allow between 1 and ${MAX_FILES_CEILING} files.`,
          { questionId: question.id, sectionId: question.sectionId },
        );
      }
      const maxFileSizeMb = maxFileSizeMbOf(question);
      if (
        !Number.isFinite(maxFileSizeMb) ||
        maxFileSizeMb < MIN_FILE_SIZE_MB ||
        maxFileSizeMb > MAX_FILE_SIZE_MB_CEILING
      ) {
        addError(
          `“${question.label}” needs a file size limit between ${MIN_FILE_SIZE_MB} and ${MAX_FILE_SIZE_MB_CEILING} MB.`,
          { questionId: question.id, sectionId: question.sectionId },
        );
      }
    }

    if (question.type === "linear_scale") {
      const min = question.config?.min ?? 1;
      const max = question.config?.max ?? 5;
      if (max <= min) {
        addError(`“${question.label}” needs a scale that goes upwards.`, {
          questionId: question.id,
          sectionId: question.sectionId,
        });
      }
      // The renderer steps by one and answers must be whole numbers, so
      // fractional bounds offer choices that can never be submitted.
      if (!Number.isInteger(min) || !Number.isInteger(max)) {
        addError(
          `“${question.label}” needs whole numbers at both ends of its scale.`,
          { questionId: question.id, sectionId: question.sectionId },
        );
      }
    }

    // A rating always starts at 1, so its one setting is the top end. Ten is
    // where the icons stop reading as a count and the question should be a
    // linear scale instead.
    if (question.type === "rating") {
      const max = question.config?.max ?? 5;
      if (!Number.isInteger(max) || max < 2 || max > 10) {
        addError(
          `“${question.label}” needs a whole number of icons between 2 and 10.`,
          { questionId: question.id, sectionId: question.sectionId },
        );
      }
    }

    // A grid without both axes is a table nobody can answer — the same
    // failure as a choice question with no options, on two dimensions. And
    // two rows or two columns reading the same is an answer nobody can
    // interpret afterwards, exactly as with duplicate options.
    if (question.type === "repeatable_group") {
      const fields = repeatFieldsOf(question);
      if (
        fields.some((field) =>
          field.validation?.some((rule) => rule.kind === "compareAnswers"),
        )
      ) {
        addError(
          `“${question.label}” cannot compare answers inside repeated entries.`,
          { questionId: question.id, sectionId: question.sectionId },
        );
      }
      if (question.required && !fields.some((field) => takesAnswer(field.type)))
        addError(
          `“${question.label}” needs a question that collects an answer before entries can be required.`,
          { questionId: question.id, sectionId: question.sectionId },
        );
      // Legacy repeatable questions share the same field validation contract.
      const children: FormContent = {
        title: question.label,
        sections: [
          { id: question.sectionId, title: question.label, pos: "a0" },
        ],
        questions: fields.map((field) => ({
          ...field,
          sectionId: question.sectionId,
        })),
      };
      for (const issue of checkPublishable(children).issues) {
        const message = `“${question.label}”: ${issue.message}`;
        if (issue.severity === "error")
          addError(message, {
            fieldId: issue.questionId,
            questionId: question.id,
            sectionId: question.sectionId,
          });
        else {
          warnings.push(message);
          issues.push({
            ...issue,
            message,
            fieldId: issue.questionId,
            questionId: question.id,
            sectionId: question.sectionId,
          });
        }
      }
      const min = question.config?.minEntries ?? 0;
      const max = question.config?.maxEntries ?? MAX_REPEAT_ENTRIES;
      const report = (message: string) =>
        addError(`“${question.label}”: ${message}`, {
          questionId: question.id,
          sectionId: question.sectionId,
        });
      if (fields.length < 1 || fields.length > MAX_REPEAT_FIELDS)
        report(`Add between 1 and ${MAX_REPEAT_FIELDS} fields.`);
      if (
        new Set(fields.map((field) => field.label.trim())).size !==
        fields.length
      )
        report("Give each field a distinct label.");
      if (
        new Set(fields.map((field) => field.id)).size !== fields.length ||
        fields.some((field) => !questionIdSchema.safeParse(field.id).success)
      )
        report("Fields must have unique, valid IDs.");
      if (
        !Number.isInteger(min) ||
        !Number.isInteger(max) ||
        min < 0 ||
        max < 1 ||
        max > MAX_REPEAT_ENTRIES ||
        min > max
      )
        report(
          `Entry limits must be whole numbers between 0 and ${MAX_REPEAT_ENTRIES}, with a maximum of at least 1 and at least the minimum.`,
        );
    }

    if (question.type === "grid") {
      const columns = optionsOf(question);
      const rows = rowsOf(question);
      if (columns.length === 0) {
        addError(`“${question.label}” has no columns to choose from.`, {
          questionId: question.id,
          sectionId: question.sectionId,
        });
      }
      if (rows.length === 0) {
        addError(`“${question.label}” has no rows to answer.`, {
          questionId: question.id,
          sectionId: question.sectionId,
        });
      }
      const columnLabels = columns.map((option) => option.label.trim());
      if (new Set(columnLabels).size !== columnLabels.length) {
        addError(`“${question.label}” lists the same column twice.`, {
          questionId: question.id,
          sectionId: question.sectionId,
        });
      }
      const rowLabels = rows.map((row) => row.label.trim());
      if (new Set(rowLabels).size !== rowLabels.length) {
        addError(`“${question.label}” lists the same row twice.`, {
          questionId: question.id,
          sectionId: question.sectionId,
        });
      }
    }

    // A text block has no answer, so a required one is a form nobody can
    // submit. The builder hides the switch, but a concurrent type change can
    // strand the flag on.
    if (question.type === "text_block" && question.required) {
      addError(
        `“${question.label}” is a text block — it can't be required, because there is nothing to answer.`,
        { questionId: question.id, sectionId: question.sectionId },
      );
    }
  }

  // A condition nobody can satisfy silently removes whatever it guards — a
  // question, a whole section, or a jump that then never fires. Checked
  // wherever a condition can be attached, not just on questions: the builder
  // puts them on sections and jumps too.
  const inOrder = questionsInFormOrder(form);
  const orderOf = (questionId: string) =>
    inOrder.findIndex((q) => q.id === questionId);

  const checkCondition = (
    condition: Condition | undefined,
    describe: string,
    /** How far through the form this condition is evaluated. */
    availableUpTo: number,
    /** The section this condition is attached to, if any. */
    sectionOfCondition: string,
    target: Pick<PublishIssue, "questionId" | "sectionId">,
  ) => {
    if (!condition) return;
    // Source existence matters for every operator: a condition on a deleted
    // question never fires, whichever comparison it makes.
    const source = form.questions.find((q) => q.id === condition.questionId);
    if (!source) {
      addError(
        `${describe} depends on a question that no longer exists.`,
        target,
      );
      return;
    }
    // A condition can only read answers the applicant has already given. One
    // pointing further ahead never sees its answer: `equals` never fires, and
    // `isEmpty` always does — so a jump keyed on a later question would skip
    // the rest of the form, silently and on every submission.
    if (orderOf(source.id) > availableUpTo) {
      addError(
        `${describe} depends on “${source.label}”, which the applicant hasn't reached yet.`,
        target,
      );
      return;
    }
    // Positive conditions can safely depend on a conditional question: the
    // walk excludes its stale answer when it is hidden. Absence-based checks
    // would instead fire for applicants who were never asked the source.
    const requiresAnswer =
      (condition.op === "equals" ||
        condition.op === "contains" ||
        condition.op === "isAnswered") &&
      !evaluateCondition(condition, {}, new Map([[source.id, source]]));
    if (source.visibleWhen && !requiresAnswer) {
      addError(
        `${describe} depends on “${source.label}”, which is itself only asked sometimes.`,
        target,
      );
      return;
    }
    const sourceSection = form.sections.find((s) => s.id === source.sectionId);
    if (sourceSection?.visibleWhen && !requiresAnswer) {
      addError(
        `${describe} depends on “${source.label}”, which sits in a section that is only shown sometimes.`,
        target,
      );
      return;
    }

    // Coming earlier in the form isn't enough — a jump can route around the
    // section the source lives in, and on that route the answer is simply
    // absent. The condition then behaves differently for different applicants,
    // with nothing to show for it.
    if (
      sectionOfCondition !== undefined &&
      source.sectionId !== sectionOfCondition &&
      !sectionsAlwaysVisitedBefore(form, sectionOfCondition).has(
        source.sectionId,
      )
    ) {
      addError(
        `${describe} depends on “${source.label}”, which some applicants never see because a jump can route around it.`,
        target,
      );
      return;
    }

    // A source with no single answer to read — a text block has none at all,
    // and a grid's is a table. The pickers exclude these, but a concurrent
    // type change can point an existing condition at one.
    if (!canConditionOn(source.type)) {
      addError(
        `${describe} depends on “${source.label}”, which is a ${definitionFor(
          source.type,
        ).label.toLowerCase()} — its answer can't steer anything.`,
        target,
      );
      return;
    }

    // The option check only applies to comparisons against a value.
    if (condition.op === "isAnswered" || condition.op === "isEmpty") return;
    if (source.type === "acknowledgment") {
      addError(
        `${describe} must check whether “${source.label}” is acknowledged or not acknowledged, rather than compare its text.`,
        target,
      );
      return;
    }
    const choices = optionsOf(source);
    const sourceOffersOptions = [
      "multiple_choice",
      "checkboxes",
      "dropdown",
    ].includes(source.type);

    // "They took the Other box" only means something on a choice question
    // that offers one. Dropping the Other box clears the conditions that
    // watch it, but one written concurrently on another client survives that
    // merge — the same seam every other stale reference here comes through.
    if (condition.compare === "other") {
      // Through `offersOther`, not the raw flag: a merge can strand
      // `allowOther` on a dropdown, which never renders an Other box.
      if (!offersOther(source)) {
        addError(
          `${describe} waits for “${source.label}” to be answered with "Other", which it no longer offers.`,
          target,
        );
      }
      return;
    }

    // A condition that names an option on a question which no longer offers
    // any. The local cleanup can't reach a condition another admin wrote
    // concurrently — there was nothing to clear when it happened — so this is
    // where it has to be caught. Left through, it compares an opaque id
    // against typed text, matches nothing, and hides what it guards without
    // ever failing.
    if (condition.compare === "option" && !sourceOffersOptions) {
      addError(
        `${describe} waits for a particular answer to “${source.label}”, which is no longer a question with options to choose from.`,
        target,
      );
      return;
    }

    const closed = choices.length > 0 && sourceOffersOptions;
    // On a choice question the condition holds an option id. Deleting an
    // option clears the conditions that named it, but one added concurrently
    // on another client survives that merge — there was nothing to clear when
    // it happened — so publishing is where it has to be caught.
    if (closed && !choices.some((option) => option.id === condition.value)) {
      addError(
        `${describe} waits for an answer to “${source.label}” that is no longer one of its options.`,
        target,
      );
    }
  };

  for (const question of form.questions) {
    // A question sees everything asked before it.
    checkCondition(
      question.visibleWhen,
      `“${question.label}”`,
      orderOf(question.id) - 1,
      question.sectionId,
      { questionId: question.id, sectionId: question.sectionId },
    );
  }
  // Count through the form rather than looking positions up, so an empty
  // section is bounded by what precedes it. Deriving the boundary from a
  // section's own questions gave an empty one the run of the whole form, and a
  // jump out of it could test a question nobody had reached — which then fires
  // immediately and can end the form before a later required question is shown.
  let askedSoFar = 0;
  for (const section of orderedSections(form)) {
    const inSection = orderedQuestions(form, section.id);
    const beforeSection = askedSoFar - 1;
    askedSoFar += inSection.length;
    const throughSection = askedSoFar - 1;
    // A section's own condition is evaluated before any of its questions are
    // asked, so it can only read earlier sections.
    checkCondition(
      section.visibleWhen,
      `The section “${section.title}”`,
      beforeSection,
      section.id,
      { sectionId: section.id },
    );
    // A jump is evaluated at the end of the section, so it may also read the
    // answers given inside it.
    for (const jump of section.jumps ?? []) {
      checkCondition(
        jump.when,
        `A jump out of “${section.title}”`,
        throughSection,
        section.id,
        { sectionId: section.id },
      );
    }
  }

  // A pattern that doesn't compile is skipped at validation time, so the rule
  // the author wrote silently does nothing.
  for (const question of form.questions) {
    for (const rule of question.validation ?? []) {
      if (rule.kind !== "regex") continue;
      try {
        new RegExp(rule.pattern);
      } catch {
        addError(
          `The pattern on “${question.label}” isn't a valid expression, so it wouldn't check anything.`,
          { questionId: question.id, sectionId: question.sectionId },
        );
        continue;
      }
      // A quantifier applied to an already-repeating group can take exponential
      // time on a long near-match. That runs on the server, so one answer would
      // stall the app for everyone — worth refusing at the point the pattern is
      // written rather than discovering it in production.
      if (isCatastrophicPattern(rule.pattern)) {
        addError(
          `The pattern on “${question.label}” can be extremely slow to check. Rewrite it without a repeat inside a repeat.`,
          { questionId: question.id, sectionId: question.sectionId },
        );
      }
    }
  }

  const graph = validateJumpGraph(form);
  errors.push(...graph.errors.map((problem) => problem.message));
  warnings.push(...graph.warnings.map((problem) => problem.message));
  issues.push(
    ...graph.errors.map((problem) => ({
      message: problem.message,
      severity: "error" as const,
      sectionId: problem.sectionIds[0],
    })),
    ...graph.warnings.map((problem) => ({
      message: problem.message,
      severity: "warning" as const,
      sectionId: problem.sectionIds[0],
    })),
  );

  return { ok: errors.length === 0, errors, warnings, issues };
}
